import { useEffect, useRef, useState, type BaseSyntheticEvent, type RefObject } from 'react';
import {
  useFormContext,
  useFormState,
  type FieldErrors,
  type FieldValues,
} from 'react-hook-form';

interface IUseAuthFormSubmitArgs<T extends FieldValues> {
  onValid: (values: T, event?: BaseSyntheticEvent) => void | Promise<void>;
  onInvalid?: (errors: FieldErrors<T>) => void;
}

export interface IUseAuthFormSubmitResult {
  submit: (event?: BaseSyntheticEvent) => Promise<void>;
  bannerVisible: boolean;
  bannerRef: RefObject<HTMLDivElement | null>;
  /** Message extracted from an error thrown by `onValid` (typically BE). */
  serverMessage: string | undefined;
  dismissBanner: () => void;
}

export function useAuthFormSubmit<T extends FieldValues>({
  onValid,
  onInvalid,
}: IUseAuthFormSubmitArgs<T>): IUseAuthFormSubmitResult {
  const { handleSubmit, control, watch } = useFormContext<T>();
  const { errors } = useFormState<T>({ control });
  const [bannerVisible, setBannerVisible] = useState(false);
  const [serverMessage, setServerMessage] = useState<string | undefined>(undefined);
  const bannerRef = useRef<HTMLDivElement>(null);
  const hadErrorsRef = useRef(false);
  const hasServerMessageRef = useRef(false);

  const errorCount = Object.keys(errors).length;
  useEffect(() => {
    if (errorCount > 0) {
      hadErrorsRef.current = true;
      return;
    }
    if (bannerVisible && hadErrorsRef.current) {
      setBannerVisible(false);
      hadErrorsRef.current = false;
    }
  }, [errorCount, bannerVisible]);

  // Server errors (e.g. wrong email/password) aren't tracked by react-hook-form,
  // so they don't clear via the validation effect above. Clear them as soon as
  // the user edits any field, instead of waiting for the next submit.
  useEffect(() => {
    hasServerMessageRef.current = serverMessage !== undefined;
  }, [serverMessage]);

  useEffect(() => {
    const subscription = watch(() => {
      if (hasServerMessageRef.current) {
        setServerMessage(undefined);
        setBannerVisible(false);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const scrollBannerIntoView = () => {
    bannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const submit = handleSubmit(
    async (values, event) => {
      setBannerVisible(false);
      setServerMessage(undefined);
      try {
        await onValid(values, event);
      } catch (err) {
        const message = err instanceof Error && err.message ? err.message : undefined;
        setServerMessage(message);
        setBannerVisible(true);
        scrollBannerIntoView();
      }
    },
    (submitErrors) => {
      setServerMessage(undefined);
      setBannerVisible(true);
      scrollBannerIntoView();
      onInvalid?.(submitErrors);
    },
  );

  return {
    submit,
    bannerVisible,
    bannerRef,
    serverMessage,
    dismissBanner: () => {
      setServerMessage(undefined);
      setBannerVisible(false);
    },
  };
}
